import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TicketReport } from "@/features/tickets/TicketReport";
import type { TicketWithRefs } from "@/features/tickets/types";

export const Route = createFileRoute("/_authenticated/reports/engineer-performance")({
  component: () => (
    <TicketReport
      id="R-50"
      title="Engineer Performance"
      description="Ticket volume, SLA compliance, and resolution counts by assigned engineer."
      enabledFilters={["branch", "department", "priority", "sla", "dateRange"]}
      filename="engineer-performance"
      buildRows={(tickets) => aggregate(tickets).map((r) => ({
        "Engineer (user id)": r.userId,
        "Total": r.total,
        "Resolved": r.resolved,
        "Closed": r.closed,
        "Breached": r.breached,
        "Within SLA": r.withinSla,
      }))}
      render={(tickets) => {
        const rows = aggregate(tickets);
        return (
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Engineer (user id)</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Resolved</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead>Breached</TableHead>
                <TableHead>Within SLA</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.userId}>
                    <TableCell className="font-mono text-xs">{r.userId}</TableCell>
                    <TableCell>{r.total}</TableCell>
                    <TableCell>{r.resolved}</TableCell>
                    <TableCell>{r.closed}</TableCell>
                    <TableCell>{r.breached}</TableCell>
                    <TableCell>{r.withinSla}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No engineer-assigned tickets.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        );
      }}
    />
  ),
});

function aggregate(tickets: TicketWithRefs[]) {
  const map = new Map<string, { userId: string; total: number; resolved: number; closed: number; breached: number; withinSla: number }>();
  for (const t of tickets) {
    if (!t.assigned_user_id) continue;
    const row = map.get(t.assigned_user_id) ?? { userId: t.assigned_user_id, total: 0, resolved: 0, closed: 0, breached: 0, withinSla: 0 };
    row.total += 1;
    if (t.status === "Resolved") row.resolved += 1;
    if (t.status === "Closed") row.closed += 1;
    if (t.sla_status === "Response Breached" || t.sla_status === "Resolution Breached") row.breached += 1;
    if (t.sla_status === "Within SLA" || t.sla_status === "Completed") row.withinSla += 1;
    map.set(t.assigned_user_id, row);
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}
